export const isPresent = <T>(x: T): x is Exclude<T, undefined | null> =>
  x != null;

export const isEdgePresent = <T extends {node: unknown}>(
  x: T | null | undefined
): x is {[K in keyof T]: K extends 'node' ? NonNullable<T[K]> : T[K]} =>
  x?.node != null;

export const hasKey = <T extends string>(
  key: T,
  v: unknown
): v is Record<T, unknown> => {
  if (typeof v !== 'object') {
    return false;
  }
  if (v == null) {
    return false;
  }
  return key in v;
};

export const hasKey2 = <V extends object>(
  v: V,
  key: unknown
): key is keyof V => {
  if (typeof key !== 'string') {
    return false;
  }
  if (v == null) {
    return false;
  }
  return key in v;
};

type ObjectWithPath<T extends string[]> = T extends [
  string,
  ...infer Rest extends string[],
]
  ? {[key in T[0]]: ObjectWithPath<Rest>}
  : unknown;

export const hasKeyPath =
  <T extends string[]>(...path: T) =>
  (v: unknown): v is ObjectWithPath<T> => {
    let current = v;
    for (const key of path) {
      if (!hasKey(key, current)) {
        return false;
      }
      current = current[key];
    }
    return true;
  };

export const hasKeys = <T extends string>(
  keys: T[],
  v: unknown
): v is Record<T, unknown> => {
  return keys.every((key) => hasKey(key, v));
};

export const asNumber = (v: unknown): number | undefined =>
  typeof v === 'number' ? v : undefined;

export const isArray = <T>(v: T): v is Extract<T, readonly unknown[]> =>
  Array.isArray(v);

export const isUnknownArray = (v: unknown): v is unknown[] => Array.isArray(v);

export const isString = <T>(v: T): v is Extract<T, readonly unknown[]> =>
  typeof v === 'string';

export const objectPresence = <T extends object>(v: T): T | undefined =>
  Object.entries(v).length !== 0 ? v : undefined;

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};
